// app_client/src/pages/admin/RoleManagement.jsx
import React, { useEffect, useState } from 'react';
import useRoleStore from '../../store/roleStore';
import { ShieldCheck, Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';

const permissionFields = [
  { key: 'read', label: 'Görüntüle' },
  { key: 'write', label: 'Düzenle' },
  { key: 'delete', label: 'Sil' },
  { key: 'assign', label: 'Atama' },
  { key: 'canViewAll', label: 'Tümünü Gör', group: 'advanced' },
  { key: 'viewAll', label: 'Tümünü Listele', group: 'advanced' }
];

const basePermissionFields = permissionFields.filter((field) => field.group !== 'advanced');
const advancedPermissionFields = permissionFields.filter((field) => field.group === 'advanced');

const getPermissionModuleId = (permission) => String(permission?.moduleId?._id || permission?.moduleId || '');

const createEmptyPermission = (moduleItem, activeField = null) => ({
  moduleId: moduleItem._id,
  moduleName: moduleItem.name,
  read: activeField === 'read',
  write: activeField === 'write',
  delete: activeField === 'delete',
  assign: activeField === 'assign',
  canViewAll: activeField === 'canViewAll',
  viewAll: activeField === 'viewAll'
});

const RoleManagement = () => {
  const { confirm } = useConfirmation();
  const { roles, modules, fetchInitialData, saveRole, deleteRole, loading } = useRoleStore();
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Yeni Rol Şablonu
  const handleAddNewRole = () => {
    const newRole = {
      name: '',
      permissions: modules.map(m => ({
        moduleId: m._id,
        moduleName: m.name,
        read: false,
        write: false,
        delete: false,
        assign: false,
        canViewAll: false,
        viewAll: false
      }))
    };
    setSelectedRole(newRole);
  };

  const handlePermissionChange = (moduleId, field) => {
    const moduleItem = modules.find((module) => module._id === moduleId);
    if (!moduleItem) return;

    let permissions = [...(selectedRole.permissions || [])];
    const index = permissions.findIndex(p => getPermissionModuleId(p) === moduleId);

    if (index > -1) {
      // Var olan yetkiyi değiştir
      permissions[index] = {
        ...createEmptyPermission(moduleItem),
        ...permissions[index],
        moduleId: permissions[index].moduleId?._id || permissions[index].moduleId || moduleId,
        moduleName: permissions[index].moduleName || moduleItem.name,
        [field]: !permissions[index][field]
      };
    } else {
      // Henüz role eklenmemiş yeni bir modülse diziye ekle
      permissions.push(createEmptyPermission(moduleItem, field));
    }
    setSelectedRole({ ...selectedRole, permissions });
  };

  const handleDeleteRole = async (e, role) => {
    e.stopPropagation();
    if (await confirm(`${role.name} rolünü silmek istediğinize emin misiniz?`)) {
      const result = await deleteRole(role._id);
      if (result.success) {
        if (selectedRole?._id === role._id) setSelectedRole(null);
        toast.success("Rol silindi");
      } else {
        toast.error(result.message || "Rol silinemedi", { duration: 6000 });
      }
    }
  };

  if (loading) return <div className="p-10 text-center font-bold">Yükleniyor...</div>;

  return (
    <div className="p-6 grid grid-cols-12 gap-6">
      {/* Sol Taraf: Rol Listesi */}
      <div className="col-span-12 md:col-span-4 bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-black text-slate-800 uppercase text-sm tracking-widest">Roller</h2>
          <button onClick={handleAddNewRole} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all">
            <Plus size={18} />
          </button>
        </div>
        <div className="space-y-2">
          {roles.map(r => (
            <div
              key={r._id}
              className={`w-full p-4 rounded-xl font-bold transition-all border flex items-center justify-between gap-3 ${
                selectedRole?._id === r._id ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200' : 'bg-slate-50 text-slate-600 border-transparent hover:border-slate-200'
              }`}
            >
              <button
                type="button"
                onClick={() => setSelectedRole(r)}
                className="flex items-center gap-3 flex-1 text-left"
              >
                <ShieldCheck size={18} />
                {r.name}
              </button>
              <button
                type="button"
                onClick={(e) => handleDeleteRole(e, r)}
                className={`p-2 rounded-lg transition-colors ${
                  selectedRole?._id === r._id
                    ? 'text-white/80 hover:bg-white/10 hover:text-white'
                    : 'text-rose-500 hover:bg-rose-50'
                }`}
                title="Rolü Sil"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Sağ Taraf: Yetki Matrisi */}
      <div className="col-span-12 md:col-span-8 bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        {selectedRole ? (
          <div>
            <div className="flex justify-between items-center mb-6">
              <input
                className="text-2xl font-black text-slate-800 outline-none border-b-2 border-transparent focus:border-blue-500 bg-transparent"
                value={selectedRole.name}
                placeholder="Rol Adı Girin..."
                onChange={(e) => setSelectedRole({ ...selectedRole, name: e.target.value })}
              />
              <button 
                onClick={() => saveRole(selectedRole)}
                className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-black shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all"
              >
                <Save size={18} /> KAYDET
              </button>
            </div>

            <div className="overflow-x-auto">
            <table className="w-full min-w-[920px]">
              <thead>
                <tr className="text-left text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                  <th className="pb-2">Modül</th>
                  <th className="pb-2 text-center" colSpan={basePermissionFields.length}>Temel Yetkiler</th>
                  <th className="pb-2 text-center bg-slate-50" colSpan={advancedPermissionFields.length}>Gelişmiş Ayarlar</th>
                </tr>
                <tr className="text-left text-xs font-black text-slate-400 uppercase tracking-widest border-b">
                  <th className="pb-4">Modül Adı</th>
                  {basePermissionFields.map(({ key, label }) => (
                    <th key={key} className="pb-4 text-center">{label}</th>
                  ))}
                  {advancedPermissionFields.map(({ key, label }) => (
                    <th key={key} className="pb-4 text-center bg-slate-50">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {modules.map((m) => {
                  // Role içindeki yetkiyi bul veya varsayılan boş yetki objesi oluştur
                  const perm = (selectedRole.permissions || []).find(p => 
                    getPermissionModuleId(p) === m._id
                  ) || createEmptyPermission(m);
                
                  return (
                    <tr key={m._id} className="group hover:bg-slate-50/50 transition-all">
                      <td className="py-4 font-bold text-slate-700">{m.label}</td>
                      {permissionFields.map(({ key }) => (
                        <td
                          key={key}
                          className={`py-4 text-center ${key === 'canViewAll' || key === 'viewAll' ? 'bg-slate-50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(perm[key])}
                            onChange={() => handlePermissionChange(m._id, key)}
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        ) : (
          <div className="h-64 flex flex-col items-center justify-center text-slate-400">
            <ShieldCheck size={48} className="mb-2 opacity-20" />
            <p className="font-bold">Düzenlemek için bir rol seçin veya yeni oluşturun.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement;
